import React, { useEffect, useState } from "react";
import { Button, Popover } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";

/** pages that have a guided tour (see hooks/TourContext) */
const PAGES_WITH_TOUR = ["indicateurs", "projections"];

const storageKey = (page: string) => `portail-climat:tour-prompt-seen:${page}`;

// storage can be unavailable (private mode, blocked site data): then never prompt rather than prompt on every visit
const promptAlreadySeen = (page: string) => {
  try {
    return window.localStorage.getItem(storageKey(page)) === "1";
  } catch {
    return true;
  }
};

const markPromptSeen = (page: string) => {
  try {
    window.localStorage.setItem(storageKey(page), "1");
  } catch {
    // nothing to do: the prompt simply won't be remembered
  }
};

type Props = {
  /** first segment of the current path, e.g. "indicateurs" */
  page: string;
  startTour: () => void;
};

/**
 * Help button starting the guided tour of the current page.
 * On the first visit of a page with a tour, a popover anchored on the button announces the tour.
 */
export default function TourButton({ page, startTour }: Props) {
  const hasTour = PAGES_WITH_TOUR.includes(page);
  const [promptOpen, setPromptOpen] = useState(false);

  useEffect(() => {
    setPromptOpen(false);
    // the button is only rendered in the desktop header
    if (!hasTour || promptAlreadySeen(page) || !window.matchMedia("(min-width: 1024px)").matches) return;
    // let the page and its map render first, the tour targets need them
    const timer = setTimeout(() => setPromptOpen(true), 1200);
    return () => clearTimeout(timer);
  }, [page, hasTour]);

  if (!hasTour) return null;

  const dismiss = () => {
    markPromptSeen(page);
    setPromptOpen(false);
  };

  const start = () => {
    dismiss();
    startTour();
  };

  return (
    <Popover
      open={promptOpen}
      placement="bottomRight"
      zIndex={6000}
      title={<span className="text-base">Première visite ?</span>}
      content={
        <div className="flex max-w-xs flex-col gap-3">
          <p className="m-0 text-sm text-gray-600">
            Une courte visite guidée vous présente les principales fonctionnalités de cette page. Vous pourrez la relancer à tout moment avec ce
            bouton.
          </p>
          <div className="flex justify-end gap-2">
            <Button onClick={dismiss}>Non merci</Button>
            <Button type="primary" onClick={start}>
              Lancer la visite
            </Button>
          </div>
        </div>
      }
    >
      {/* icon only on narrow desktop screens, where the header has no room for the label */}
      <Button
        type="primary"
        shape="round"
        icon={<QuestionCircleOutlined />}
        onClick={start}
        aria-label="Visite guidée"
        title="Visite guidée"
        className="font-semibold shadow-sm"
      >
        <span className="hidden xl:inline">Visite guidée</span>
      </Button>
    </Popover>
  );
}
